// Global "Спросить Orca" bar: one calm input surface at the bottom of every
// page that opens into a panel (⌘K). It knows what is on screen (open news,
// event, transfer, position, video, digest, coin…): a small label names it and
// the suggested question sits inside the field as a smart placeholder that
// softly crossfades. The width is fixed — nothing jumps when the text changes.
// This is also how the web covers the bot's News Impact. Conversations are
// saved to the Orca AI history; questions spend the one shared AI quota.
import { useT } from '@/i18n';
import i18n from '@/i18n';
import { useEffect, useRef, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router';
import { useBodyClass } from '@/app/providers/bodyClass';
import { XButton } from '@/components/ui/Buttons';
import { Icon } from '@/components/ui/Icon';
import { useOrcaAIData } from '@/hooks/queries';
import { cx } from '@/lib/format';
import { reduceMotion } from '@/lib/random';
import { closeTopLayer, useDetailOpen, useLayer } from '@/lib/overlay';
import { useUpgrade } from '@/features/plans/Upgrade';
import { PLAN_LABEL, useCaps } from '@/hooks/useCaps';

/** Questions about Orca's plans: answered even when the daily quota is used up (they cost nothing). */
export function productChips(plan: 'free' | 'plus' | 'premium', planName: string, trial: boolean) {
  if (plan === 'premium') return [i18n.t('ai:product.myPremium'), i18n.t('ai:product.diff', { a: 'Premium', b: 'Plus' })];
  const next = PLAN_LABEL[plan === 'free' ? 'plus' : 'premium'];
  return [i18n.t('ai:product.whatGives', { plan: next }), i18n.t('ai:product.diff', { a: next, b: planName }), ...(trial ? [i18n.t('ai:product.trial')] : [])];
}
import { useOrca } from './OrcaProvider';
import type { AskCtx } from './contexts';
import { Thread, WelcomeMessage } from './OrcaAnswer';

export const Eq = () => <span className="eq"><i /><i /><i /><i /><i /></span>;
export const PlusGlyph = () => <svg viewBox="0 0 24 24"><path d="M12 5v14M5 12h14" /></svg>;
export const StopGlyph = () => <span className="stop-sq" aria-hidden="true" />;
const SendGlyph = () => <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M12 19V5M6 11l6-6 6 6" /></svg>;

/** The small context label: «BTC · позиция», «Протокол FOMC», «ETH · Whale», «Wall Street»… */
export function ctxShort(c: AskCtx) {
  const head = c.label.split(' · ')[0];
  switch (c.kind) {
    case 'event': return head;
    case 'position': return `${c.tag} · ${i18n.t('ai:short.position')}`;
    case 'whale': return `${c.tag} · OrcaWatch`;
    case 'digest': case 'ws': return 'Wall Street';
    case 'news': return c.icon === 'ai' ? i18n.t('ai:short.idea') : `${c.tag} · ${i18n.t('ai:short.news')}`;
    case 'chart': return `${c.tag} · ${i18n.t('ai:short.chart')}`;
    case 'alert': return `${c.tag} · ${i18n.t('ai:short.alert')}`;
    case 'post': return `${c.tag} · X`;
    case 'video': return 'YouTube';
    default: return c.tag;
  }
}

export function AskBar() {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const { askContexts } = useOrcaAIData();
  const { bar, chats, openBar, closeBar, askInBar, askAbout, clearBarCtx, suggest, finish, limitReached, resetAt, screenCtx, used, limit, allowed } = useOrca();
  const page = askContexts[pathname] ?? askContexts['/'];
  const chat = chats.find((c) => c.id === bar.chatId);
  const items = chat?.items ?? [];
  const pending = items.find((x) => x.state === 'pending');
  // contextual pool: the object on screen, otherwise the page's questions
  const caps = useCaps();
  const upgrade = useUpgrade();
  const { t } = useT('ai');
  // at the limit only questions about plans and features remain (free); a locked preview's own questions first
  const product = productChips(caps.plan, caps.planName, caps.trialAvailable);
  const lockedPool = screenCtx?.answer.action?.kind === 'upgrade' ? screenCtx.suggestions : [];
  const pool = limitReached ? [...new Set([...lockedPool, ...product])] : screenCtx ? screenCtx.suggestions : page.suggestions.filter(allowed);
  const poolKey = `${screenCtx ? screenCtx.label : page.name}${limitReached ? ':limit' : ''}`;
  const [chips, setChips] = useState<string[]>(pool);
  const [hintIdx, setHintIdx] = useState(0);
  const [value, setValue] = useState('');
  const [min, setMin] = useState(false);
  const bodyRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  useBodyClass('ask-open', bar.open);
  // Orca is a modal layer: over a detail view it reuses the detail's scrim (no second backdrop)
  useLayer('orca', bar.open, closeBar);
  // a detail view has its own «Спросить Orca»: the global bar steps aside (its state stays)
  const detail = useDetailOpen();

  const ask = (q: string) => { askInBar(q); setChips(suggest(pool, q)); };
  /** a suggested question: with the on-screen object when there is no conversation yet */
  const askSuggested = (q: string) => {
    if (!chat && screenCtx) { askAbout(screenCtx, q); setChips(pool.filter((x) => x !== q)); return; }
    ask(q);
  };

  useEffect(() => { setChips(pool); setHintIdx(0); }, [poolKey]); // eslint-disable-line react-hooks/exhaustive-deps
  // the pill's hint rotates through the pool (not the same question all the time)
  useEffect(() => {
    if (bar.open || pool.length < 2 || reduceMotion()) return;
    const t = window.setInterval(() => setHintIdx((i) => (i + 1) % pool.length), 6000);
    return () => window.clearInterval(t);
  }, [bar.open, pool.length, poolKey]);
  const hint = pool[hintIdx % Math.max(1, pool.length)];
  // soft crossfade: the previous suggestion fades out under the new one
  const [shown, setShown] = useState<{ cur?: string; prev?: string }>({ cur: hint });
  // new data from the server (another language…): no crossfade from the old wording
  const shownData = useRef(askContexts);
  useEffect(() => {
    const fresh = shownData.current !== askContexts;
    shownData.current = askContexts;
    setShown((s) => (s.cur === hint ? s : fresh ? { cur: hint } : { cur: hint, prev: s.cur }));
  }, [hint, askContexts]);
  const label = screenCtx ? ctxShort(screenCtx) : page.name;
  const line = chat ? t('bar.continue', { title: chat.title }) : limitReached ? t('bar.limitAgain', { time: resetAt }) : undefined;
  useEffect(() => {
    if (bar.open) { const t = window.setTimeout(() => inputRef.current?.focus(), 60); return () => window.clearTimeout(t); }
  }, [bar.open]);
  // note: scrollTo may return a Promise in newer browsers — never return it from an effect
  const toBottom = () => { bodyRef.current?.scrollTo({ top: bodyRef.current.scrollHeight, behavior: reduceMotion() ? 'auto' : 'smooth' }); };
  useEffect(() => { toBottom(); }, [items.length]);

  // ⌘K / Esc
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        if (pathname.startsWith('/ai')) { (document.getElementById('aiInput') as HTMLInputElement | null)?.focus(); return; }
        if (bar.open) closeBar(); else openBar();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  });
  // tuck into a small capsule while scrolling down
  useEffect(() => {
    let lastY = window.scrollY;
    const onScroll = () => { const y = window.scrollY; setMin(y > 240 && y > lastY); lastY = y; };
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const toAI = () => { closeBar(); navigate(chat ? `/ai?chat=${chat.id}` : '/ai'); };
  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (pending && bar.chatId) { finish(bar.chatId, pending.id, 'stopped'); return; }
    const v = value.trim();
    if (!v) return;
    setValue('');
    ask(v);
  };

  return (
    <>
      <div className="ask-scrim" onClick={closeTopLayer} />
      <div className={cx('ask', min && 'min', detail && 'over-detail')}>
        <section className="ask-panel" role="dialog" aria-modal={bar.open} aria-label={t('ask')} inert={!bar.open}>
          <header className="ask-h">
            <span className={cx('ask-ctx', bar.ctx && 'item')}>
              {bar.ctx ? <><Icon name={bar.ctx.icon} />{t('bar.context', { label: bar.ctx.label })}</> : <><i />{t('bar.page', { name: page.name })}</>}
            </span>
            {caps.plan === 'premium'
              ? <span className={cx('ask-quota', limitReached && 'full')} title={t('bar.quotaHint', { time: resetAt })}>{t('bar.quota', { n: Math.min(used, limit), max: limit })}</span>
              : <button type="button" className={cx('ask-quota', limitReached && 'full')} title={t('bar.quotaHint', { time: resetAt })} onClick={() => { closeBar(); upgrade('more_ai'); }}>{t('bar.quota', { n: Math.min(used, limit), max: limit })} · {t('bar.moreIn', { plan: PLAN_LABEL[caps.plan === 'free' ? 'plus' : 'premium'] })}</button>}
            <XButton label={chat ? t('bar.continueAi') : t('bar.openAi')} inv sm className="ask-full" onClick={toAI} />
            <button className="ask-x" type="button" aria-label={t('common:a11y.close')} onClick={closeBar}><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6 6 18" /></svg></button>
          </header>
          <div className={cx('ask-body', items.length > 0 && 'has')} ref={bodyRef}>
            {chat ? <Thread chatId={chat.id} items={items} onSettled={toBottom} onLink={closeBar} /> : <div className="ask-empty"><WelcomeMessage /></div>}
          </div>
          <div className="ai-chips">{(limitReached ? pool : chips).map((q) => <button key={q} type="button" onClick={() => askSuggested(q)}>{q}</button>)}</div>
          {bar.ctx && (
            <div className="ctx-card">
              <span className="ctx-ic"><Icon name={bar.ctx.icon} /></span>
              <span className="ctx-t"><small>{t('considers')}</small><b>{bar.ctx.label}</b></span>
              <button type="button" aria-label={t('bar.removeCtx')} onClick={clearBarCtx}><Icon name="close" /></button>
            </div>
          )}
          {limitReached && <p className="ask-limit"><Icon name="alert" />{t('bar.limitLine', { time: resetAt })}</p>}
          <form className={cx('cs-cmp ask-cmp', (value.trim() || pending) && 'has')} autoComplete="off" onSubmit={submit}>
            <Link className="cmp-plus" to={chat ? `/ai?chat=${chat.id}` : '/ai'} aria-label={t('bar.openAi')} onClick={closeBar}><PlusGlyph /></Link>
            <div className="cmp-field"><input ref={inputRef} type="text" placeholder={limitReached ? t('bar.limitPh') : bar.ctx ? t('bar.refine', { tag: bar.ctx.tag }) : hint ?? t('bar.askPh')} aria-label={t('inputAria')} value={value} onChange={(e) => setValue(e.target.value)} /></div>
            <button type="submit" className={cx('cmp-go', pending && 'stop')} aria-label={pending ? t('stopAria') : t('send')}>{pending ? <StopGlyph /> : <Eq />}</button>
          </form>
        </section>
        <div className={cx('askp', screenCtx && 'has-ctx', limitReached && 'limit')} role="group" aria-label={`${t('ask')} (⌘K)`} inert={detail || bar.open} aria-hidden={detail || undefined}>
          <button type="button" className="askp-field" onClick={openBar} aria-label={screenCtx ? t('bar.askAbout', { label: screenCtx.label }) : t('ask')}>
            <span className="askp-mark" aria-hidden="true"><img src="/assets/brand/orca-mark.png" alt="" /></span>
            <span className="askp-lines">
              <span className="askp-ctx"><i aria-hidden="true" />{label}</span>
              <span className="askp-q" aria-live="off">
                {line ? <span className="askp-t">{line}</span> : (
                  <>
                    {shown.prev && <span key={`p${shown.prev}`} className="askp-t out" aria-hidden="true">{shown.prev}</span>}
                    <span key={`c${shown.cur}`} className="askp-t in">{shown.cur ?? t('bar.askMarket')}</span>
                  </>
                )}
              </span>
            </span>
            <kbd>⌘K</kbd>
          </button>
          <button type="button" className="askp-send" aria-label={hint && !chat && !limitReached ? t('bar.askHint', { q: hint }) : t('bar.open')}
            onClick={() => { if (chat || limitReached || !hint) { openBar(); return; } askSuggested(hint); }}><SendGlyph /></button>
        </div>
      </div>
    </>
  );
}
